import { type Security, isNonNullable } from '@ipfabric/utils';

export type TestReferenceKey = string;

export type SecurityReferenceResolveError = 'circular' | 'notFound';

export const isSecurityReferenceResolvedError = (
  reference?: ResolvedSecurityReference | null,
): reference is SecurityReferenceResolveError =>
  reference === 'circular' || reference === 'notFound';

export type ResolvedSecurityReference =
  | Exclude<Security.Test, Security.ReferencedTest>
  | SecurityReferenceResolveError;

/**
 * After loading device security from server, pre-resolve all test references
 * as this is a tricky issue (reference may point to another reference, there may
 * be cycles etc).
 */
export type ResolvedReferencedTest = Security.ReferencedTest & {
  resolvedReference: ResolvedSecurityReference;
  /**
   * This field always contains the last key under which was the resolvedReference found,
   * even if this is a ReferencedTest pointing to another ReferencedTest.
   * This makes detection of cycles easier.
   * In case of circular reference error, this contains a key of current reference.
   * In case of not found reference, this contains the last key that was not found.
   * Null = This is a ReferencedTest node but reference resolution has failed.
   * @see resolvedReference
   */
  resolvedReferenceKey: TestReferenceKey | null;
};

export const isResolvedReferencedTest = (
  test: Security.ReferencedTest,
): test is ResolvedReferencedTest =>
  isNonNullable((test as ResolvedReferencedTest).resolvedReference);
